import { useMemo, useState, type FormEvent } from 'react';
import {
  api,
  type AdminUser,
  type FlagDefinition,
  type FlagFamily,
  type FlagRule,
  type FlagScope,
  type FlagType,
  type FlagValue,
} from '../api';
import { useAction, useLoader } from '../hooks';
import { Alert } from './Alert';
import { ConfirmDialog } from './ConfirmDialog';
import { Field } from './Field';
import { FLAG_FAMILY_HINTS, FLAG_FAMILY_LABELS, FLAG_TYPE_LABELS, SCOPE_LABELS, SCOPE_ORDER } from './labels';
import { Tag } from './Tag';

const KEY_RE = /^[a-z0-9][a-z0-9._-]{0,63}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function formatValue(v: FlagValue): string {
  if (typeof v === 'boolean') return v ? 'vrai' : 'faux';
  if (typeof v === 'string') return `« ${v} »`;
  return String(v);
}

/** Convertit la saisie d'un champ selon le type du flag (les nombres sont des entiers). */
export function parseValue(
  type: FlagType,
  raw: string,
): { ok: true; value: FlagValue } | { ok: false; error: string } {
  if (type === 'boolean') return { ok: true, value: raw === 'true' };
  if (type === 'string') return { ok: true, value: raw };
  if (!/^-?\d+$/.test(raw.trim())) return { ok: false, error: 'Entier attendu.' };
  const n = Number(raw.trim());
  if (!Number.isSafeInteger(n)) return { ok: false, error: 'Entier trop grand.' };
  return { ok: true, value: n };
}

const rawOf = (v: FlagValue): string => String(v);

function ValueField({
  label,
  type,
  value,
  onChange,
  error,
}: {
  label: string;
  type: FlagType;
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
}) {
  return (
    <Field label={label} error={error}>
      {(p) =>
        type === 'boolean' ? (
          <select
            {...p}
            value={value === 'true' ? 'true' : 'false'}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="true">vrai</option>
            <option value="false">faux</option>
          </select>
        ) : (
          <input
            {...p}
            type="text"
            value={value}
            inputMode={type === 'number' ? 'numeric' : undefined}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      }
    </Field>
  );
}

/** Rappel de l'ordre de résolution, du plus au moins prioritaire. */
function ResolutionOrder() {
  return (
    <div className="plan" role="note" aria-label="Priorité de résolution">
      <h3>Priorité de résolution pour une instance</h3>
      <ol className="priority">
        {SCOPE_ORDER.map((s) => (
          <li key={s}>
            <strong>{SCOPE_LABELS[s]}</strong>
          </li>
        ))}
        <li>
          <strong>Valeur par défaut</strong> du flag
        </li>
      </ol>
      <p className="muted">
        La première règle qui s'applique à l'instance l'emporte : une règle d'instance prime sur celle de
        l'utilisateur, qui prime sur celle du canal, puis sur la règle globale. Une règle avec un pourcentage
        ne s'applique qu'à cette part des instances (tirage stable pour une instance et une clé donnée) ; les
        autres passent à la portée suivante. Une valeur de flag est signée mais lisible par l'agent : jamais
        de secret.
      </p>
    </div>
  );
}

// --- règles ---------------------------------------------------------------------------------------------------------

function RuleForm({
  flag,
  users,
  busy,
  onSubmit,
}: {
  flag: FlagDefinition;
  users: AdminUser[] | null;
  busy: boolean;
  onSubmit: (rule: {
    scope: FlagScope;
    scopeId?: string;
    value: FlagValue;
    percentage: number | null;
  }) => void;
}) {
  const [scope, setScope] = useState<FlagScope>('global');
  const [scopeId, setScopeId] = useState('');
  const [value, setValue] = useState(rawOf(flag.type === 'boolean' ? true : flag.defaultValue));
  const [percentage, setPercentage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const instances = (users ?? []).flatMap((u) =>
    u.instances
      .filter((i) => i.status !== 'removed')
      .map((i) => ({
        id: i.id,
        label: `${u.username} — ${i.kind === 'cloud' ? 'cloud' : 'local'} (${i.channel})`,
      })),
  );

  function changeScope(next: FlagScope) {
    setScope(next);
    setScopeId(next === 'channel' ? 'stable' : '');
    setErrors({});
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    const parsed = parseValue(flag.type, value);
    if (!parsed.ok) next.value = parsed.error;
    if (scope !== 'global' && !scopeId.trim()) next.scopeId = 'Cible obligatoire pour cette portée.';
    if ((scope === 'user' || scope === 'instance') && scopeId.trim() && !UUID_RE.test(scopeId.trim()))
      next.scopeId = 'Identifiant (UUID) attendu.';
    let pct: number | null = null;
    if (percentage.trim() !== '') {
      pct = Number(percentage);
      if (!Number.isInteger(pct) || pct < 0 || pct > 100) next.percentage = 'Entier entre 0 et 100.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0 || !parsed.ok) return;
    onSubmit({
      scope,
      ...(scope === 'global' ? {} : { scopeId: scopeId.trim() }),
      value: parsed.value,
      percentage: pct,
    });
  }

  const targetOptions =
    scope === 'user'
      ? (users ?? []).map((u) => ({ id: u.id, label: `${u.displayName || u.username} (${u.username})` }))
      : instances;

  return (
    <form className="form-grid" onSubmit={submit} noValidate aria-label="Ajouter ou remplacer une règle">
      <Field label="Portée">
        {(p) => (
          <select {...p} value={scope} onChange={(e) => changeScope(e.target.value as FlagScope)}>
            {[...SCOPE_ORDER].reverse().map((s) => (
              <option key={s} value={s}>
                {SCOPE_LABELS[s]}
              </option>
            ))}
          </select>
        )}
      </Field>
      {scope === 'channel' && (
        <Field label="Canal" error={errors.scopeId}>
          {(p) => (
            <select {...p} value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
              <option value="stable">stable</option>
              <option value="canary">canary</option>
            </select>
          )}
        </Field>
      )}
      {(scope === 'user' || scope === 'instance') && (
        <Field
          label={scope === 'user' ? 'Utilisateur' : 'Instance'}
          error={errors.scopeId}
          hint={
            targetOptions.length === 0 ? "Liste indisponible : saisissez l'identifiant (UUID)." : undefined
          }
        >
          {(p) =>
            targetOptions.length > 0 ? (
              <select {...p} value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
                <option value="">— choisir —</option>
                {targetOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                {...p}
                type="text"
                value={scopeId}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setScopeId(e.target.value)}
              />
            )
          }
        </Field>
      )}
      <ValueField label="Valeur" type={flag.type} value={value} onChange={setValue} error={errors.value} />
      <Field
        label="Déploiement progressif (%)"
        hint="Vide : la règle s'applique toujours. 25 : à environ un quart des instances, les autres passent à la portée suivante."
        error={errors.percentage}
      >
        {(p) => (
          <input
            {...p}
            type="text"
            inputMode="numeric"
            value={percentage}
            autoComplete="off"
            onChange={(e) => setPercentage(e.target.value)}
          />
        )}
      </Field>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Enregistrer la règle
        </button>
        <span className="muted">Remplace la règle existante de même portée et de même cible.</span>
      </div>
    </form>
  );
}

function FlagSheet({
  flag,
  users,
  onChanged,
  onDeleted,
}: {
  flag: FlagDefinition;
  users: AdminUser[] | null;
  onChanged: () => Promise<void>;
  onDeleted: () => Promise<void>;
}) {
  const edit = useAction();
  const rule = useAction();
  const del = useAction();
  const [family, setFamily] = useState<FlagFamily>(flag.family);
  const [defaultValue, setDefaultValue] = useState(rawOf(flag.defaultValue));
  const [description, setDescription] = useState(flag.description);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [removingRule, setRemovingRule] = useState<FlagRule | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [typed, setTyped] = useState('');

  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users ?? []) {
      map.set(u.id, u.displayName ? `${u.displayName} (${u.username})` : u.username);
      for (const i of u.instances) map.set(i.id, `${u.username} — ${i.kind === 'cloud' ? 'cloud' : 'local'}`);
    }
    return map;
  }, [users]);

  const rules = [...flag.rules].sort(
    (a, b) =>
      SCOPE_ORDER.indexOf(a.scope) - SCOPE_ORDER.indexOf(b.scope) ||
      (a.scopeId ?? '').localeCompare(b.scopeId ?? ''),
  );

  async function save(e: FormEvent) {
    e.preventDefault();
    const parsed = parseValue(flag.type, defaultValue);
    if (!parsed.ok) {
      setDefaultError(parsed.error);
      return;
    }
    setDefaultError(null);
    if (
      (await edit.run(() =>
        api.updateFlag(flag.key, { family, defaultValue: parsed.value, description: description.trim() }),
      )) !== undefined
    )
      await onChanged();
  }

  async function saveRule(r: {
    scope: FlagScope;
    scopeId?: string;
    value: FlagValue;
    percentage: number | null;
  }) {
    if ((await rule.run(() => api.putFlagRule(flag.key, r))) !== undefined) await onChanged();
  }

  async function dropRule(r: FlagRule) {
    const ok = await rule.run(async () => {
      await api.deleteFlagRule(flag.key, r.id);
      return true;
    });
    if (ok) await onChanged();
    setRemovingRule(null);
  }

  async function dropFlag() {
    const ok = await del.run(async () => {
      await api.deleteFlag(flag.key);
      return true;
    });
    if (ok) await onDeleted();
    setConfirmDelete(false);
    setTyped('');
  }

  const targetLabel = (r: FlagRule): string => {
    if (r.scope === 'global') return 'Toutes les instances';
    if (r.scope === 'channel') return `Canal ${r.scopeId ?? ''}`;
    return (r.scopeId && names.get(r.scopeId)) || r.scopeId || '—';
  };

  return (
    <section className="panel stack" aria-labelledby={`flag-${flag.key}`}>
      <div className="page-head">
        <h2 id={`flag-${flag.key}`}>
          Flag <code>{flag.key}</code> <span className="muted">({FLAG_TYPE_LABELS[flag.type]})</span>
        </h2>
        <button
          type="button"
          className="btn btn-small btn-danger-outline"
          onClick={() => {
            setTyped('');
            setConfirmDelete(true);
          }}
        >
          Supprimer le flag
        </button>
      </div>
      {del.error && <Alert>{del.error}</Alert>}

      <form
        className="form-grid"
        onSubmit={(e) => void save(e)}
        noValidate
        aria-label="Modifier la définition"
      >
        <Field label="Famille" hint={FLAG_FAMILY_HINTS[family]}>
          {(p) => (
            <select {...p} value={family} onChange={(e) => setFamily(e.target.value as FlagFamily)}>
              {(Object.keys(FLAG_FAMILY_LABELS) as FlagFamily[]).map((f) => (
                <option key={f} value={f}>
                  {FLAG_FAMILY_LABELS[f]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <ValueField
          label="Valeur par défaut"
          type={flag.type}
          value={defaultValue}
          onChange={setDefaultValue}
          error={defaultError}
        />
        <Field label="Description">
          {(p) => (
            <textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          )}
        </Field>
        {edit.error && <Alert>{edit.error}</Alert>}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={edit.busy}>
            Enregistrer la définition
          </button>
        </div>
      </form>

      <h3>Règles</h3>
      <ResolutionOrder />
      {rule.error && <Alert>{rule.error}</Alert>}
      {rules.length === 0 ? (
        <p className="muted">
          Aucune règle : toutes les instances reçoivent la valeur par défaut ({formatValue(flag.defaultValue)}
          ).
        </p>
      ) : (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">
              Règles du flag {flag.key}, de la plus à la moins prioritaire
            </caption>
            <thead>
              <tr>
                <th scope="col">Priorité</th>
                <th scope="col">Portée</th>
                <th scope="col">Cible</th>
                <th scope="col">Valeur</th>
                <th scope="col">Déploiement</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id}>
                  <th scope="row">{SCOPE_ORDER.indexOf(r.scope) + 1}</th>
                  <td>{SCOPE_LABELS[r.scope]}</td>
                  <td className="url">{targetLabel(r)}</td>
                  <td>
                    {typeof r.value === 'boolean' ? (
                      <Tag tone={r.value ? 'green' : 'gray'}>{formatValue(r.value)}</Tag>
                    ) : (
                      <code>{formatValue(r.value)}</code>
                    )}
                  </td>
                  <td>
                    {r.percentage === null ? (
                      <span className="muted">toujours</span>
                    ) : (
                      <span title="Part des instances concernées ; les autres passent à la portée suivante">
                        {r.percentage} %
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-small btn-danger-outline"
                      onClick={() => setRemovingRule(r)}
                    >
                      Supprimer{' '}
                      <span className="sr-only">
                        la règle {SCOPE_LABELS[r.scope]} {targetLabel(r)}
                      </span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <details className="disclosure" open={rules.length === 0}>
        <summary>Ajouter une règle</summary>
        <RuleForm flag={flag} users={users} busy={rule.busy} onSubmit={(r) => void saveRule(r)} />
      </details>

      {removingRule && (
        <ConfirmDialog
          title="Supprimer la règle"
          confirmLabel="Supprimer la règle"
          danger
          busy={rule.busy}
          onConfirm={() => void dropRule(removingRule)}
          onCancel={() => setRemovingRule(null)}
        >
          <p>
            Supprimer la règle <strong>{SCOPE_LABELS[removingRule.scope]}</strong> (
            {targetLabel(removingRule)}) du flag <code>{flag.key}</code> ? Les instances concernées passeront
            à la portée suivante.
          </p>
        </ConfirmDialog>
      )}
      {confirmDelete && (
        <ConfirmDialog
          title="Supprimer le flag"
          confirmLabel="Supprimer le flag"
          danger
          busy={del.busy}
          confirmDisabled={typed !== flag.key}
          onConfirm={() => void dropFlag()}
          onCancel={() => setConfirmDelete(false)}
        >
          <p>
            Le flag <strong>{flag.key}</strong> et ses {flag.rules.length} règle(s) seront supprimés ; les
            plugins qui le lisent retrouveront leur valeur par défaut.
          </p>
          <Field label={`Retapez ${flag.key} pour confirmer`}>
            {(p) => (
              <input
                {...p}
                type="text"
                value={typed}
                autoFocus
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setTyped(e.target.value)}
              />
            )}
          </Field>
        </ConfirmDialog>
      )}
    </section>
  );
}

// --- onglet ---------------------------------------------------------------------------------------------------------

function CreateFlagForm({
  busy,
  error,
  onSubmit,
}: {
  busy: boolean;
  error: string | null;
  onSubmit: (input: {
    key: string;
    type: FlagType;
    defaultValue: FlagValue;
    family: FlagFamily;
    description: string;
  }) => void;
}) {
  const [key, setKey] = useState('');
  const [type, setType] = useState<FlagType>('boolean');
  const [family, setFamily] = useState<FlagFamily>('entitlement');
  const [value, setValue] = useState('false');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  function changeType(next: FlagType) {
    setType(next);
    setValue(next === 'boolean' ? 'false' : next === 'number' ? '0' : '');
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!KEY_RE.test(key.trim())) next.key = 'Minuscules, chiffres, « . _ - » (ex. plugin.kanban).';
    const parsed = parseValue(type, value);
    if (!parsed.ok) next.value = parsed.error;
    setErrors(next);
    if (Object.keys(next).length > 0 || !parsed.ok) return;
    onSubmit({ key: key.trim(), type, defaultValue: parsed.value, family, description: description.trim() });
  }

  return (
    <form className="form-grid" onSubmit={submit} noValidate>
      <Field label="Clé" hint="Non modifiable ensuite." error={errors.key}>
        {(p) => (
          <input
            {...p}
            type="text"
            value={key}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            onChange={(e) => setKey(e.target.value)}
          />
        )}
      </Field>
      <Field label="Type" hint="Non modifiable ensuite ; les nombres sont des entiers.">
        {(p) => (
          <select {...p} value={type} onChange={(e) => changeType(e.target.value as FlagType)}>
            {(Object.keys(FLAG_TYPE_LABELS) as FlagType[]).map((t) => (
              <option key={t} value={t}>
                {FLAG_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Famille" hint={FLAG_FAMILY_HINTS[family]}>
        {(p) => (
          <select {...p} value={family} onChange={(e) => setFamily(e.target.value as FlagFamily)}>
            {(Object.keys(FLAG_FAMILY_LABELS) as FlagFamily[]).map((f) => (
              <option key={f} value={f}>
                {FLAG_FAMILY_LABELS[f]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <ValueField
        label="Valeur par défaut"
        type={type}
        value={value}
        onChange={setValue}
        error={errors.value}
      />
      <Field label="Description">
        {(p) => (
          <textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      {error && <Alert>{error}</Alert>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Créer le flag
        </button>
      </div>
    </form>
  );
}

export function FlagsTab() {
  const { data: flags, error, reload } = useLoader(api.flags);
  const { data: users } = useLoader(api.adminUsers);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const create = useAction();
  const selected = flags?.find((f) => f.key === selectedKey) ?? null;

  return (
    <div className="stack">
      <div className="page-head">
        <p className="muted">
          Flags résolus par WHCC et envoyés à chaque instance dans son manifeste signé ; l'agent les expose
          aux plugins sans redémarrage.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Annuler' : 'Nouveau flag'}
        </button>
      </div>

      {creating && (
        <section className="panel" aria-label="Nouveau flag">
          <h2>Nouveau flag</h2>
          <CreateFlagForm
            busy={create.busy}
            error={create.error}
            onSubmit={(input) =>
              void create
                .run(() => api.createFlag(input))
                .then(async (f) => {
                  if (f) {
                    setCreating(false);
                    setSelectedKey(f.key);
                    await reload();
                  }
                })
            }
          />
        </section>
      )}

      {error && <Alert>{error}</Alert>}
      {flags === null && !error && (
        <p className="muted" aria-busy="true">
          Chargement des flags…
        </p>
      )}
      {flags?.length === 0 && (
        <div className="empty">
          <p>Aucun flag défini.</p>
          <p className="muted">Créez un flag pour piloter une fonction sans redéployer les instances.</p>
        </div>
      )}
      {flags && flags.length > 0 && (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Définitions de flags</caption>
            <thead>
              <tr>
                <th scope="col">Clé</th>
                <th scope="col">Famille</th>
                <th scope="col">Type</th>
                <th scope="col">Par défaut</th>
                <th scope="col">Règles</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.key} className={f.key === selectedKey ? 'row-selected' : undefined}>
                  <th scope="row" className="user-cell">
                    <div>
                      <code>{f.key}</code>
                    </div>
                    {f.description && <div className="muted">{f.description}</div>}
                  </th>
                  <td>{FLAG_FAMILY_LABELS[f.family]}</td>
                  <td>{FLAG_TYPE_LABELS[f.type]}</td>
                  <td>{formatValue(f.defaultValue)}</td>
                  <td>{f.rules.length}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-small"
                      aria-pressed={f.key === selectedKey}
                      onClick={() => setSelectedKey(f.key === selectedKey ? null : f.key)}
                    >
                      {f.key === selectedKey ? 'Masquer' : 'Détail'}{' '}
                      <span className="sr-only">du flag {f.key}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <FlagSheet
          key={selected.key}
          flag={selected}
          users={users}
          onChanged={reload}
          onDeleted={async () => {
            setSelectedKey(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}
